import { isBarakhadiAkshara, getGuninthaluSpeechText, CONSONANT_STEM, MATRA_VOWEL, INDEPENDENT_VOWELS, CONJUNCTS } from './barakhadiPhonetics';

const STANDALONE_AUDIO_KEY = (w: string): string | undefined => {
  const k = INDEPENDENT_VOWELS[w] || CONJUNCTS[w];
  return k ? k.replace('ī', 'ii') : undefined;
};
const mp3Failed = new Set<string>();
import {
  applySafeProsody,
  clampRate,
  isApplePlatform,
  isWindowsPlatform,
  pickEnglishCueVoice,
  pickHindiVoice,
  safePitch,
  whenVoicesReady,
} from './speechPlatform';
import { getSavedVoiceName, resolveVoiceForRole } from './voiceConfig';
import { isMacDesktopPlatform, macBarakhadiCue, pickMacBarakhadiVoice } from './macBarakhadiSpeech';
import { isDandaOrVerseNumberToken, stripDandaForSpeech } from './dandaSpeech';
import { expandDigitsInLessonText } from './lessonNumberSpeech';

// Native Web Speech API pronunciation helper for Sanskrit text only.
// Strips punctuation plus Devanagari digits and hyphens (e.g. the
// numbers guide's "० - शून्यम्" button labels) while preserving word-separating spaces.
const cleanWord = (value: string): string =>
  value
    .replace(/[।॥,;:!?()[\]{}<>'"“”‘’\-–—०-९./\\=+#*~_`]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isSanskritText = (value: string): boolean => /[\u0900-\u097F]/.test(value);

// Devanagari vowel signs (matras) that can carry a visarga's "echo" vowel.
const VOWEL_MATRAS = ['ा', 'ि', 'ी', 'ु', 'ू', 'ृ', 'ॄ', 'ॢ', 'ॣ', 'े', 'ै', 'ो', 'ौ'];
const VOWEL_TO_MATRA: Record<string, string> = {
  'आ': 'ा', 'इ': 'ि', 'ई': 'ी', 'उ': 'ु', 'ऊ': 'ू',
  'ऋ': 'ृ', 'ॠ': 'ॄ', 'ऌ': 'ॢ', 'ॡ': 'ॣ',
  'ए': 'े', 'ऐ': 'ै', 'ओ': 'ो', 'औ': 'ौ',
};

// Windows speech engines misread roman cues for bare Varṇamālā consonants;
// keep the complete standard consonant row in Devanagari so hi-IN is used.
const WINDOWS_BARE_CONSONANTS = new Set(
  'क ख ग घ ङ च छ ज झ ञ ट ठ ड ढ ण त थ द ध न प फ ब भ म य र ल व श ष स ह'.split(' '),
);
// Bare conjunct tiles: Hindi Devanagari on every OS (Mac roman ksha/jnya/sh-ra
// and doubled त्र त्र sound worse than the Windows hi-IN letter voice).
const CONJUNCT_TILES = new Set(['क्ष', 'ज्ञ', 'त्र', 'श्र']);

// Exact/substring phonetic overrides for words that speech engines
// otherwise mispronounce or misinterpret entirely.
const applyWordOverrides = (word: string): string => {
  // ङ example words: stable English cues (same every click).
  if (word === 'अङ्गम्' || word === 'अंगम्') return 'an gam';
  if (word === 'गङ्गा' || word === 'गंगा') return 'gun ga';
  if (word === 'रङ्गः' || word === 'रंगः' || word === 'रङ्ग' || word === 'रंग') return 'run ga';
  // 'नव' (nava, 9) is otherwise auto-corrected by some engines to the English
  // month "November"; force a pure Devanagari override to keep it Sanskrit.
  if (word.includes('नव')) return word.replace(/नव/g, 'नवम्');
  // 'सप्त' (sapta, 7) gets clipped to "sat" without the plosive 'p'; a
  // hyphenated romanized hint forces the engine to articulate it in full.
  if (word === 'सप्त') return 'sap-ta';

  // Preschool shape names / examples: split compounds and use anusvāra so
  // hi-IN voices don't slur conjuncts (ṣaṭkoṇa, śaṅku, hṛdayākāra …).
  const shapeHints: Record<string, string> = {
    'षट्कोणम्': 'षट् कोणम्',
    'पञ्चकोणम्': 'पंच कोणम्',
    'त्रिकोणम्': 'त्रि कोणम्',
    'समचतुर्भुजम्': 'सम चतुर् भुजम्',
    'हृदयाकारः': 'हृदय आकारः',
    'अण्डाकारः': 'अण्ड आकारः',
    'अर्धचन्द्रः': 'अर्ध चंद्रः',
    'अर्धवृत्तम्': 'अर्ध वृत्तम्',
    'चतुरस्रम्': 'चतुर् अस्रम्',
    'शङ्कुः': 'शंकुः',
    'पतङ्गः': 'पतंगः',
    'मधुकोषः': 'मधु कोषः',
    'चित्रफलकम्': 'चित्र फलकम्',
  };
  if (shapeHints[word]) return shapeHints[word];

  // Māheśvara Sūtra phoneme expansions: articulates individual atomic sounds with spaces
  // rather than letting the TTS engine blend root vowels/letters into an artificial word.
  if (word === 'अइउण्') return 'अ इ उ ण्';
  if (word === 'ऋऌक्') return isMacDesktopPlatform() ? 'रि ल्रि क्' : 'ऋ लृ क्';
  if (word === 'एओङ्') return 'ए ओ ङ्';
  if (word === 'ऐऔच्') return 'ऐ औ च्';
  if (word === 'हयवरट्') return 'ह य व र ट्';
  if (word === 'लण्') return 'ल ण्';
  if (word === 'ञमङणनम्') return 'ञ म ङ ण न म्';
  if (word === 'झभञ्') return 'झ भ ञ्';
  if (word === 'घढधष्') return 'घ ढ ध ष्';
  if (word === 'जबगडदश्') return 'ज ब ग ड द श्';
  if (word === 'खफछठथचटतव्') return 'ख फ छ ठ थ च ट त व्';
  if (word === 'कपय्') return 'क प य्';
  if (word === 'शषसर्') return 'श ष स र्';
  if (word === 'हल्') return 'ह ल्';

  // Avagraha in Asato Ma mantra: comma forces a brief breath pause so the TTS engine
  // distinctly articulates the initial 'a' in amṛtam instead of swallowing it into -mā.
  if (word.includes('मृत्योर्मा')) {
    return word
      .replace(/मृत्योर्मा\s*ऽ?\s*अ?मृत[ंम्]/g, 'मृत्योर्मा, अमृतम्');
  }

  return word;
};

// Visarga (ः) is a breathy 'h' echo of the preceding vowel, e.g. अर्थः -> अर्थह,
// मातुः -> मातुहु. Voice engines otherwise drop it or mispronounce it silently.
// On Windows, a thin space before 'ह' helps SAPI keep the echo audible as one word
// (fixes picture words like एणः, ईशः, उष्ट्रः, डमरुः on Chrome/Edge).
const applyVisargaEcho = (word: string, spacedEcho = false): string => {
  if (!word.endsWith('ः')) return word;
  const base = word.slice(0, -1);
  const lastChar = base[base.length - 1];
  const h = spacedEcho ? ' ह' : 'ह';

  if (VOWEL_MATRAS.includes(lastChar)) {
    return base + h + lastChar;
  }
  if (VOWEL_TO_MATRA[lastChar]) {
    return base + h + VOWEL_TO_MATRA[lastChar];
  }
  // Bare consonant (inherent 'a') or the vowel 'अ': 'ह' already carries the 'a' sound.
  return base + h;
};

/**
 * Rare vocalic picture words use the Windows Devanagari + spaced-visarga path
 * on Apple too, so ॠ / ऌ are not rewritten to री / ली on Mac.
 */
const isRareVocalicWord = (word: string): boolean =>
  word === 'ॠकारः' || word.startsWith('ॠकार') || word === 'ऌकारः' || word.startsWith('ऌकार');

const usesWindowsWordSpeech = (word: string): boolean =>
  isWindowsPlatform() || (isApplePlatform() && isRareVocalicWord(word));

// Builds the text actually sent to the speech engine: word overrides + visarga echo.
// Full words stay Devanagari (Hindi voice). Single tiles use roman cues.
// Bare conjuncts always use Devanagari + hi-IN (Mac + Windows). On Windows,
// bare consonants also skip roman cues — SAPI garbles those.
const toSpeechText = (word: string): string => {
  if (CONJUNCT_TILES.has(word)) {
    return word;
  }
  // Single बारहखड़ी (Guṇintālu) / Varṇamālā tiles: return authentic Devanagari speech text
  if (isBarakhadiAkshara(word) || WINDOWS_BARE_CONSONANTS.has(word)) {
    return getGuninthaluSpeechText(word);
  }

  // Convert any numerals into Sanskrit Devanagari words so raw digits never reach the speech engine
  let workingWord = word;
  if (/[0-9०-९]/.test(workingWord)) {
    workingWord = expandDigitsInLessonText(workingWord);
  }

  const overridden = applyWordOverrides(workingWord);

  // Windows whole-word path: keep Devanagari + spaced visarga echo for hi-IN.
  // Mac rare-vocalic words use this same path instead of a री / ली rewrite.
  if (usesWindowsWordSpeech(word)) {
    // Never let roman overrides win for ordinary picture words on Windows —
    // force Devanagari so pickHindiVoice is used (एणः, ईशः, …).
    if (/^[a-z\- ]+$/i.test(overridden) && !/^(an gam|gun ga|run ga|sap-ta)$/i.test(overridden)) {
      return applyVisargaEcho(word, true);
    }
    if (/^[a-z\- ]+$/i.test(overridden)) {
      return overridden; // intentional ङ / सप्त roman anchors
    }
    return applyVisargaEcho(overridden, true);
  }

  return applyVisargaEcho(overridden, false);
};

/** Default playback rate (1x). Slow presets were removed. */
const DEFAULT_RATE = 1;

/** Rough Devanagari akṣara count (spoken-length proxy). */
export const countSpeechUnits = (text: string): number => {
  if (!/[\u0900-\u097F]/.test(text)) return Math.max(1, text.trim().length);
  let units = 0;
  for (const ch of text) {
    const c = ch.codePointAt(0)!;
    // independent vowels, consonants, extra consonants/vowels
    if ((c >= 0x0904 && c <= 0x0939) || c === 0x093D || (c >= 0x0958 && c <= 0x0961) || (c >= 0x0972 && c <= 0x097F)) units += 1;
  }
  return Math.max(1, units);
};

/** Expected speaking time in ms for text at a given rate. */
export const estimateSpeakMs = (text: string, rate = 1): number => {
  const ms = Math.round((220 + countSpeechUnits(text) * 310) / Math.max(0.5, rate));
  return Math.min(12000, Math.max(400, ms));
};

const configureUtterance = (utterance: SpeechSynthesisUtterance, word: string, speech: string): void => {
  const voices = window.speechSynthesis.getVoices();
  const configuredReaderVoice = resolveVoiceForRole('reader', voices);
  const voice = configuredReaderVoice || pickHindiVoice(voices);
  utterance.voice = voice || null;

  // Devanagari text (Guninthalu, Varnamala, full Sanskrit words): always use Indian/Hindi voice
  if (/[\u0900-\u097F]/.test(speech)) {
    utterance.voice = voice || null;
    utterance.lang = voice?.lang || 'hi-IN';
  } else if (/^[a-z\- ]+$/i.test(speech)) {
    const en = pickEnglishCueVoice(voices);
    if (en) {
      utterance.voice = en;
      utterance.lang = en.lang;
    } else {
      utterance.lang = 'en-IN';
    }
  } else {
    utterance.lang = voice?.lang || 'hi-IN';
  }

  // Windows: known-problem conjuncts — mild Devanagari rate, natural pitch
  const isKsha = word === 'क्ष' || speech === 'क्ष';
  const isGya = word === 'ज्ञ' || speech === 'ज्ञ';
  const isTra = word === 'त्र' || speech === 'त्र';
  const isGha = word === 'घ' || speech === 'gha';
  const isJa = word === 'ज' || speech === 'ज';
  const isDha = word === 'ध' || speech === 'ध';
  const isJha = word === 'झ' || speech === 'झ';

  if (isWindowsPlatform() && (isGya || isTra || isKsha || isGha || isJa || isDha || isJha)) {
    utterance.rate = clampRate(0.9);
    utterance.pitch = safePitch(1);
    utterance.volume = 1;
    return;
  }

  // Natural pitch (1.0) across all akṣaras — avoid chipmunk distortions
  utterance.pitch = 1.0;

  // Pedagogical recitation rates:
  // - Long vowels (ā, ī, ū, ai, au): deliberate 2-mātrā duration (0.84)
  // - Visarga echo (kaha, khaha, gaha): 0.88
  // - Conjuncts (ksha, tra, jnya, shra): 0.88
  // - Guninthalu / Varnamala single akṣaras: 0.92
  // - Full multi-word sentences: DEFAULT_RATE (1.0)
  const units = countSpeechUnits(word);
  const isTile = units <= 1;
  const isLongVowel = isTile && /[ाीूॄैौ]/.test(word) || word === 'आ' || word === 'ई' || word === 'ऊ' || word === 'ऐ' || word === 'औ';
  const isVisarga = isTile && word.endsWith('ः') || speech.endsWith('ह');
  const isConjunct = CONJUNCT_TILES.has(word) || isKsha || isGya || isTra;
  const isNgaWord = speech === 'gun ga' || speech === 'run ga' || speech === 'an gam';

  if (isNgaWord) {
    utterance.rate = 0.55;
  } else if (isVisarga) {
    utterance.rate = 0.88;
  } else if (isLongVowel) {
    utterance.rate = 0.84;
  } else if (isConjunct) {
    utterance.rate = 0.88;
  } else if (isBarakhadiAkshara(word) || WINDOWS_BARE_CONSONANTS.has(word)) {
    utterance.rate = 0.92;
  } else {
    utterance.rate = DEFAULT_RATE;
    // Pace full words by length: long compounds not rushed, short particles not drawn out.
    if (!/\s/.test(speech.trim())) {
      if (units >= 6) utterance.rate = Math.min(utterance.rate, 0.86);
      else if (units <= 2) utterance.rate = Math.min(1, Math.max(utterance.rate, 0.95));
    }
  }

  utterance.volume = 1;
  applySafeProsody(utterance);
};

/** ञ = enya with fast en then slow ya (Mac). Windows: plain Devanagari + hi-IN. */
const playNyaEnya = (onDone?: () => void): void => {
  if (isWindowsPlatform()) {
    const hi = pickHindiVoice();
    const utterance = new SpeechSynthesisUtterance('ञ');
    utterance.voice = hi || null;
    utterance.lang = hi?.lang || 'hi-IN';
    utterance.rate = clampRate(0.9);
    utterance.pitch = safePitch(1);
    utterance.volume = 1;
    utterance.onend = () => onDone?.();
    utterance.onerror = () => onDone?.();
    window.speechSynthesis.speak(utterance);
    return;
  }

  const enVoice = pickEnglishCueVoice();
  const enPart = new SpeechSynthesisUtterance('enn');
  enPart.voice = enVoice || null;
  enPart.lang = enVoice?.lang || 'en-IN';
  enPart.rate = clampRate(1.95);
  enPart.pitch = safePitch(1);
  const yaPart = new SpeechSynthesisUtterance('ya');
  yaPart.voice = enVoice || null;
  yaPart.lang = enVoice?.lang || 'en-IN';
  yaPart.rate = clampRate(0.55);
  yaPart.pitch = safePitch(1);
  enPart.onend = () => {
    window.speechSynthesis.speak(yaPart);
  };
  yaPart.onend = () => onDone?.();
  yaPart.onerror = () => onDone?.();
  enPart.onerror = () => {
    window.speechSynthesis.speak(yaPart);
  };
  window.speechSynthesis.speak(enPart);
};

let pronunciationMuted = false;
if (typeof window !== 'undefined') {
  try {
    if (localStorage.getItem('sanskrit_sound_muted') === 'true') {
      pronunciationMuted = true;
    }
  } catch {
    // Ignore storage unavailability in incognito/SSR
  }
}

export const setPronunciationMuted = (muted: boolean): void => {
  pronunciationMuted = muted;
  if (muted) {
    stopPronunciation();
  }
};

export const isPronunciationMuted = (): boolean => pronunciationMuted;

/** Bumps on every play/stop so stale whenVoicesReady() callbacks do not speak. */
let speakGeneration = 0;

/** Current stop/play generation — lets independent players (e.g. the Śānti
 * mantra reciter) notice when any other playback called stopPronunciation(). */
export const getSpeechGeneration = (): number => speakGeneration;

/** Letter MP3 currently playing (so Stop / the next item can silence it). */
let activeLetterAudio: HTMLAudioElement | null = null;

export const stopPronunciation = (): void => {
  speakGeneration += 1;
  if (activeLetterAudio) {
    try { activeLetterAudio.pause(); } catch { /* ignore */ }
    activeLetterAudio = null;
  }
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
};


/**
 * Mac-only बारहखड़ी overrides (see macBarakhadiSpeech.ts for the cell → cue table).
 * Returns true when it spoke. Only fires on macOS desktop, while the बारहखड़ी lesson
 * is open, for the specific reported cells, and only if a Hindi voice exists.
 */
const speakMacBarakhadiOverride = (word: string, onEnd?: () => void): boolean => {
  const cue = macBarakhadiCue(word);
  if (!cue) return false;
  const voices = window.speechSynthesis.getVoices();
  const savedName = getSavedVoiceName('reader');
  const saved = savedName ? voices.find((v) => v.name === savedName) : undefined;
  const hindi = pickMacBarakhadiVoice(voices, saved);
  if (!hindi) return false;
  const utterance = new SpeechSynthesisUtterance(cue);
  configureUtterance(utterance, word, cue); // same rate rules as other tiles, pitch 1.0
  utterance.voice = hindi;
  utterance.lang = hindi.lang || 'hi-IN';
  utterance.pitch = 1.0;
  if (onEnd) {
    utterance.onend = onEnd;
    utterance.onerror = onEnd;
  }
  window.speechSynthesis.speak(utterance);
  return true;
};

/**
 * Mac-only vocalic vowel tiles (Varṇamālā ऋ ॠ ऌ, plus ॡ if ever shown).
 * Plain ऋ / ॠ / लृ all came out as the same "ri" from Lekha on Mac, so each tile
 * gets an explicit hi-IN cue and ॠ / ॡ a slower rate for the long vowel:
 *   ऋ → रि   (short ri, rate from configureUtterance: 0.92)
 *   ॠ → री   (long rī, rate 0.72)
 *   ऌ → ल्रि  (lṛ ≈ "lri", rate 0.92)
 *   ॡ → ल्री  (long lṝ ≈ "lrī", rate 0.72)
 * Exact single-letter tokens only: the ॠकारः / ऌकारः name words (Windows-matched
 * path), the barakhadi C्ऋ cues, Windows and iOS are unaffected. Pitch 1.0.
 */
const MAC_VOCALIC_CUES: Record<string, { cue: string; rate?: number }> = {
  'ऋ': { cue: 'रि' },
  'ॠ': { cue: 'री', rate: 0.72 },
  'ऌ': { cue: 'ल्रि' },
  'ॡ': { cue: 'ल्री', rate: 0.72 },
};

const speakMacVocalicVowel = (word: string, onEnd?: () => void): boolean => {
  const entry = MAC_VOCALIC_CUES[word];
  if (!entry || !isMacDesktopPlatform()) return false;
  const voices = window.speechSynthesis.getVoices();
  const savedName = getSavedVoiceName('reader');
  const saved = savedName ? voices.find((v) => v.name === savedName) : undefined;
  const hindi = pickMacBarakhadiVoice(voices, saved);
  if (!hindi) return false;
  const utterance = new SpeechSynthesisUtterance(entry.cue);
  configureUtterance(utterance, word, entry.cue);
  utterance.voice = hindi;
  utterance.lang = hindi.lang || 'hi-IN';
  utterance.pitch = 1.0;
  if (entry.rate) utterance.rate = entry.rate;
  if (onEnd) {
    utterance.onend = onEnd;
    utterance.onerror = onEnd;
  }
  window.speechSynthesis.speak(utterance);
  return true;
};

const applyCallerRate = (utterance: SpeechSynthesisUtterance, rate?: number): void => {
  // Dhātupāṭha only, and only when that page passes a rate. Do not clamp:
  // the Windows 0.75–1.05 clamp made Slow / Normal / Fast sound almost the same.
  // Pitch stays 1. Bodhi and Varṇamālā never pass this rate.
  if (rate == null || !Number.isFinite(rate)) return;
  utterance.rate = rate;
  utterance.pitch = 1;
};

// Hold a reference so Chrome does not garbage-collect an in-flight utterance (lost onend / silence).
let activeUtterance: SpeechSynthesisUtterance | null = null;
export const getActiveUtterance = (): SpeechSynthesisUtterance | null => activeUtterance;
const speakConfigured = (word: string, onEnd?: (ev?: unknown) => void, plain = false, rate?: number): void => {
  if (pronunciationMuted) {
    if (onEnd) setTimeout(() => onEnd(), 0);
    return;
  }

  // Lesson cardinals are already Devanagari number-words. Skip letter MP3s,
  // roman cues, and the नव/सप्त overrides so "द्वि" stays द्वि.
  if (plain) {
    const spokenText = word
      .replace(/मृत्योर्मा\s*ऽ?\s*अ?मृत[ंम्]/g, 'मृत्योर्मा, अमृतम्')
      .replace(/ऽ/g, ' अ')
      .replace(/अमृतं/g, 'अमृतम्')
      .replace(/\s+/g, ' ')
      .trim();
    const utterance = new SpeechSynthesisUtterance(spokenText);
    configureUtterance(utterance, word, spokenText);
    applyCallerRate(utterance, rate);
    // Cardinals must use the Hindi voice. A saved English reader voice says "two".
    const hindi = pickHindiVoice();
    if (hindi) {
      utterance.voice = hindi;
      utterance.lang = hindi.lang || 'hi-IN';
    } else {
      utterance.lang = 'hi-IN';
    }
    if (onEnd) {
      utterance.onend = onEnd;
      utterance.onerror = onEnd;
    }
    activeUtterance = utterance;
    window.speechSynthesis.speak(utterance);
    try {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    } catch {
      /* ignore */
    }
    return;
  }

  // 1. Intercept Barakhadi characters and play our local MP3 files
  const standaloneKey = STANDALONE_AUDIO_KEY(word.trim());
  if (standaloneKey && !mp3Failed.has(standaloneKey)) {
    const audio = new Audio(`/audio/barakhadi/${standaloneKey}.mp3`);
    activeLetterAudio = audio;
    let done = false;
    const fallback = () => {
      if (done) return;
      done = true;
      mp3Failed.add(standaloneKey);
      if (activeLetterAudio === audio) activeLetterAudio = null;
      speakConfigured(word, onEnd, plain, rate);
    };
    audio.onended = () => { if (done) return; done = true; if (onEnd) onEnd(); };
    audio.onerror = fallback;
    audio.play().catch(err => {
      if ((err as DOMException)?.name === 'NotAllowedError') { if (!done) { done = true; if (onEnd) onEnd(); } return; }
      fallback();
    });
    return;
  }
  if (isBarakhadiAkshara(word)) {
    const cons = word[0];
    const rest = word.slice(1);
    
    // Map the Devanagari back to our English filenames
    const stem = CONSONANT_STEM[cons];
    const vowel = MATRA_VOWEL[rest] || (rest === '' ? 'a' : undefined);

    if (stem && vowel) {
      const audioPath = `/audio/barakhadi/${stem}_${vowel}.mp3`;
      const audio = new Audio(audioPath);
      activeLetterAudio = audio;
      
      if (onEnd) {
        audio.onended = onEnd;
        audio.onerror = onEnd;
      }
      
      audio.play().catch(err => {
        console.error(`Could not play audio for ${word}:`, err);
        if (onEnd) onEnd();
      });
      
      return; // Stop here so it doesn't trigger the robotic Mac voice!
    }
  }

  // 2. Fall back to the built-in Text-to-Speech for full words and sentences
  if (speakMacBarakhadiOverride(word, onEnd)) return;
  if (speakMacVocalicVowel(word, onEnd)) return;
  if (word === 'ञ') {
    const voices = window.speechSynthesis.getVoices();
    const hasHindi = !!(resolveVoiceForRole('reader', voices) || pickHindiVoice(voices));
    if (!hasHindi && !isWindowsPlatform()) {
      playNyaEnya(onEnd);
      return;
    }
  }
  const speech = toSpeechText(word);
  const utterance = new SpeechSynthesisUtterance(speech);
  configureUtterance(utterance, word, speech);
  applyCallerRate(utterance, rate);
  if (onEnd) {
    utterance.onend = onEnd;
    utterance.onerror = onEnd;
  }
  activeUtterance = utterance;
  window.speechSynthesis.speak(utterance);
  try {
    if (window.speechSynthesis.paused) window.speechSynthesis.resume();
  } catch {
    /* ignore */
  }
};
export const playPronunciation = (
  value: string,
  options?: {
    /** Hindi voice, Devanagari as written. Skips letter MP3s and roman cues. */
    plainDevanagari?: boolean;
    /** Playback rate for this call only. Ignored unless plainDevanagari. Pitch unchanged. */
    rate?: number;
    /** Story narration: slower kid-friendly pace for multi-word lines. */
    narration?: boolean;
    /** Override STORY_NARRATION_RATE for this narration call. */
    narrationRate?: number;
  },
): void => {
  if (pronunciationMuted) return;
  // Never voice a bare daṇḍa / double daṇḍa / verse number ("danda", "poorn viraam").
  if (isDandaOrVerseNumberToken(value)) return;

  // Intercept numerals (e.g. "२", "२.", "1", "4") and expand to Sanskrit cardinal/ordinal words
  let raw = value;
  if (/[0-9०-९]/.test(raw)) {
    raw = expandDigitsInLessonText(raw);
  }

  const word = cleanWord(raw) || raw.trim();
  if (!word || !isSanskritText(word) || typeof window === 'undefined' || !window.speechSynthesis) {
    return;
  }

  stopPronunciation();
  const gen = speakGeneration;
  void whenVoicesReady().then(() => {
    if (pronunciationMuted || gen !== speakGeneration) return;
    const plain = options?.plainDevanagari === true;
    const narrationRate = options?.narration && /\s/.test(word.trim()) ? (options?.narrationRate ?? STORY_NARRATION_RATE) : undefined;
    speakConfigured(word, undefined, plain, plain ? (options?.rate ?? narrationRate) : narrationRate);
  });
};

/** Kid-friendly story narration pace (was DEFAULT_RATE 1.0). */
export const STORY_NARRATION_RATE = 0.8;

/** Speak a story line slowly; single-word taps keep their normal rate. */
/** Breath pause between sentences of a story line (ms). */
export const STORY_SENTENCE_PAUSE_MS = 700;

/** Split a story line on sentence ends only (। ॥ ! ? newline) — never on commas. */
const splitStorySentences = (text: string): string[] =>
  text
    .split(/[।॥!?\n]+/)
    .map((s) => s.replace(/[0-9०-९]+\s*$/, '').replace(/\s+/g, ' ').trim())
    .filter((s) => /[\u0900-\u097F\p{L}]/u.test(s));

export const playStoryLine = (text: string, narrationRate?: number): void => {
  const sentences = splitStorySentences(text);
  if (sentences.length <= 1) {
    playPronunciation(text, { narration: true, narrationRate });
    return;
  }
  playSequence(sentences, {
    plainDevanagari: true,
    gapMs: STORY_SENTENCE_PAUSE_MS,
    rate: narrationRate ?? STORY_NARRATION_RATE,
  });
};

/** Slower pace for the Pañcabhūta (Five Friends) Sanskrit story lines. */
export const PANCHABHUTA_NARRATION_RATE = 0.65;

/** Speak a list of words/letters in order. Returns stop(). */
export const playSequence = (
  values: string[],
  options?: {
    gapMs?: number;
    onDone?: () => void;
    /**
     * Fired just before each item is spoken (for tile highlight). `index` counts
     * spoken items only; `sourceIndex` is the item's position in `values`.
     */
    onItem?: (word: string, index: number, sourceIndex: number) => void;
    /**
     * Lesson reader only. Bare numerals become Sanskrit cardinals and are
     * spoken as Devanagari (plain), not as Hindi digit names.
     */
    sanskritCardinals?: boolean;
    /** Speak every item as given Devanagari, with no letter-audio or roman cues. */
    plainDevanagari?: boolean;
    /**
     * Playback rate for plain Devanagari items only. A function is read per item
     * so a page can change speed mid-queue. Pitch is not touched. Omitted = existing rates.
     */
    rate?: number | (() => number);
  },
): (() => void) => {
  const gapMs = options?.gapMs ?? 220;
  const items: { word: string; plain: boolean; source: number }[] = [];
  for (const [source, value] of values.entries()) {
    // Skip daṇḍa / double daṇḍa / verse-number tokens so Play-all never says "danda".
    if (isDandaOrVerseNumberToken(value)) continue;
    if (options?.plainDevanagari) {
      const phrase = expandDigitsInLessonText(value).replace(/\s+/g, ' ').trim();
      if (phrase) items.push({ word: phrase, plain: true, source });
      continue;
    }
    // Lesson reader: any digit inside the line, not only a numeral-only token.
    if (options?.sanskritCardinals && /[0-9०-९]/.test(value)) {
      const phrase = stripDandaForSpeech(expandDigitsInLessonText(value)).replace(/\s+/g, ' ').trim();
      if (phrase) items.push({ word: phrase, plain: true, source });
      continue;
    }
    const word = cleanWord(value) || value.trim();
    if (word && isSanskritText(word)) {
      items.push({ word, plain: false, source });
    }
  }

  if (!items.length || typeof window === 'undefined' || !window.speechSynthesis) {
    options?.onDone?.();
    return () => undefined;
  }

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let index = 0;
  let settledForIndex = -1;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const stop = () => {
    cancelled = true;
    clearTimer();
    stopPronunciation();
  };

  const speakNext = () => {
    if (cancelled) return;
    if (index >= items.length) {
      options?.onDone?.();
      return;
    }
    const item = items[index];
    const word = item.word;
    const itemIndex = index;
    index += 1;
    options?.onItem?.(word, itemIndex, item.source);

    const after = () => {
      if (cancelled || settledForIndex === itemIndex) return;
      // External stopPronunciation() bumps speakGeneration — abort the queue.
      if (gen !== speakGeneration) {
        cancelled = true;
        return;
      }
      settledForIndex = itemIndex;
      clearTimer();
      timer = setTimeout(speakNext, gapMs);
    };

    const callerRate = (() => {
      if (options?.rate == null) return undefined;
      const raw = typeof options.rate === 'function' ? options.rate() : options.rate;
      return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : undefined;
    })();

    // Some Chrome/Edge builds skip onend; advance after a safe upper bound.
    // Generous safety bound only; onend/onerror is the primary advance.
    const rateForEstimate = callerRate ?? 1;
    // Single letters (MP3 or TTS) need a floor, or the safety timer fires before the
    // sound ends and the next letter talks over / cuts off the previous one.
    const fallbackMs = Math.max(item.plain ? 0 : 2200, Math.round(estimateSpeakMs(word, rateForEstimate) * 2.4));
    fallbackTimer = setTimeout(after, fallbackMs);
    // Chrome/Edge often drop (or instantly error with "interrupted"/"canceled")
    // a TTS utterance queued right after speechSynthesis.cancel(). Letter MP3 rows
    // were unaffected, so only the TTS rows (vowels, conjuncts) went silent.
    // Retry such an instant failure once, after a short settle.
    let startedAt = Date.now();
    let retries = 0;
    const onEnd = (ev?: unknown) => {
      const err = (ev as { error?: string } | undefined)?.error;
      if (retries < 2 && err && err !== 'not-allowed' && Date.now() - startedAt < 600
        && !cancelled && gen === speakGeneration && settledForIndex !== itemIndex) {
        retries += 1;
        setTimeout(() => {
          if (cancelled || gen !== speakGeneration || settledForIndex === itemIndex) return;
          startedAt = Date.now();
          speakConfigured(word, onEnd, item.plain, callerRate);
        }, 200);
        return;
      }
      after();
    };
    if (pronunciationMuted) {
      // speakConfigured returns without calling onEnd when muted; do not stall.
      setTimeout(after, 0);
      return;
    }
    speakConfigured(word, onEnd, item.plain, callerRate);
  };

  stopPronunciation();
  const gen = speakGeneration;
  // Speak item 0 SYNCHRONOUSLY inside the caller's click stack. Safari only lets
  // speechSynthesis start within a user gesture; awaiting whenVoicesReady() or a
  // settle setTimeout before the first utterance pushed it outside the gesture, so
  // TTS-only rows (vowels, अं/अः/अँ, conjuncts) stayed silent while MP3 rows played.
  // Once the engine is speaking, later items chain via onend (+gap) and are allowed.
  // A Chrome cancel->speak instant error is still covered by the retry in onEnd.
  speakNext();
  return stop;
};

export type BilingualSpeechItem = { sanskrit: string; english?: string };

type BilingualStep = {
  /** Index in the caller's items array (not the flattened step list). */
  itemIndex: number;
  kind: 'sa' | 'en';
  text: string;
  /** First spoken step of this item — highlight starts here. */
  notify: boolean;
};

/**
 * English glosses must not go through speakConfigured / isSanskritText.
 * That filter drops Latin sentences, and "He/She/It" would miss the English voice.
 * Rate 1, pitch 1, en-IN when no English cue voice is installed.
 */
const speakEnglishGloss = (text: string, onEnd?: () => void): void => {
  if (pronunciationMuted) return;
  const voices = window.speechSynthesis.getVoices();
  const voice = pickEnglishCueVoice(voices);
  const utterance = new SpeechSynthesisUtterance(text);
  if (voice) {
    utterance.voice = voice;
    utterance.lang = voice.lang || 'en-IN';
  } else {
    utterance.voice = null;
    utterance.lang = 'en-IN';
  }
  utterance.rate = 1;
  utterance.pitch = 1;
  utterance.volume = 1;
  applySafeProsody(utterance);
  if (onEnd) {
    utterance.onend = onEnd;
    utterance.onerror = onEnd;
  }
  window.speechSynthesis.speak(utterance);
};

const bilingualSteps = (items: BilingualSpeechItem[]): BilingualStep[] => {
  const steps: BilingualStep[] = [];
  items.forEach((item, itemIndex) => {
    const sanskrit = (item.sanskrit ?? '').replace(/\s+/g, ' ').trim();
    const english = (item.english ?? '').replace(/\s+/g, ' ').trim();
    // Bare daṇḍa / verse-number tokens are punctuation, never spoken.
    const sanskritSpeak = sanskrit && !isDandaOrVerseNumberToken(sanskrit) ? sanskrit : '';
    const first = steps.length;
    if (sanskritSpeak) {
      steps.push({ itemIndex, kind: 'sa', text: sanskritSpeak, notify: false });
    }
    if (english) {
      steps.push({ itemIndex, kind: 'en', text: english, notify: false });
    }
    if (steps.length > first) steps[first].notify = true;
  });
  return steps;
};

/**
 * Speak each item as Devanagari (Hindi, plain) then its English gloss.
 * Returns stop(), which cancels timers and any in-flight speech.
 * A short gap separates Sanskrit from English and one item from the next.
 * Empty / whitespace English is skipped. Does not change playPronunciation or playSequence.
 */
export const playBilingualSequence = (
  items: BilingualSpeechItem[],
  options?: {
    gapMs?: number;
    onItem?: (index: number) => void;
    onDone?: () => void;
  },
): (() => void) => {
  const gapMs = options?.gapMs ?? 250;
  stopPronunciation();
  const gen = speakGeneration;

  if (pronunciationMuted || typeof window === 'undefined' || !window.speechSynthesis) {
    options?.onDone?.();
    return () => {
      stopPronunciation();
    };
  }

  const steps = bilingualSteps(items);
  if (!steps.length) {
    options?.onDone?.();
    return () => {
      stopPronunciation();
    };
  }

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let index = 0;
  let settledKey = '';

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const stop = () => {
    cancelled = true;
    clearTimer();
    stopPronunciation();
  };

  const speakNext = () => {
    if (cancelled) return;
    if (gen !== speakGeneration) {
      cancelled = true;
      clearTimer();
      return;
    }
    if (index >= steps.length) {
      options?.onDone?.();
      return;
    }
    const step = steps[index];
    const stepKey = String(index);
    index += 1;
    if (step.notify) options?.onItem?.(step.itemIndex);

    const after = () => {
      if (cancelled || settledKey === stepKey) return;
      if (gen !== speakGeneration) {
        cancelled = true;
        clearTimer();
        return;
      }
      settledKey = stepKey;
      clearTimer();
      timer = setTimeout(speakNext, gapMs);
    };

    // Chrome/Edge sometimes skip onend. English glosses are full sentences, so
    // the bound grows with the text (same idea as playSequence) and cannot stall.
    const perChar = step.kind === 'en' ? 220 : 420;
    const fallbackMs =
      step.kind === 'sa'
        ? Math.round(estimateSpeakMs(step.text) * 2.4)
        : Math.max(2500, step.text.length * perChar);
    fallbackTimer = setTimeout(after, fallbackMs);
    if (step.kind === 'sa') {
      // plain=true: Hindi voice, Devanagari as written, pitch 1, no letter MP3s or roman cues.
      speakConfigured(step.text, after, true);
    } else {
      speakEnglishGloss(step.text, after);
    }
  };

  void whenVoicesReady().then(() => {
    if (cancelled || gen !== speakGeneration) return;
    speakNext();
  });
  return stop;
};

/** Lesson reader: speak Sanskrit cardinals already written in Devanagari. */
export const playLessonCardinals = (words: string[]): void => {
  playSequence(words, { plainDevanagari: true, gapMs: 80 });
};

/**
 * Lesson reader speech. Digit runs anywhere in the line become Devanagari
 * cardinals before the engine sees them. Lines with no digits keep the
 * existing word voice (including Varṇamālā MP3s, which do not call this).
 */
export const playLessonText = (value: string): void => {
  if (pronunciationMuted) return;
  if (isDandaOrVerseNumberToken(value)) return;
  if (!/[0-9०-९]/.test(value || '')) {
    playPronunciation(value);
    return;
  }
  const speech = stripDandaForSpeech(expandDigitsInLessonText(value)).replace(/\s+/g, ' ').trim();
  if (!speech || typeof window === 'undefined' || !window.speechSynthesis) return;
  playSequence([speech], { plainDevanagari: true, gapMs: 0 });
};

/* -------------------------------------------------------------------------
 * Bodhi's teacher voice
 * -------------------------------------------------------------------------
 * playPronunciation() is tuned for single letters/words (and strips spaces).
 * Bodhi speaks greetings, phrases and whole subhāṣita verses, so he gets his
 * own calm, unhurried delivery: same hi-IN voice selection + platform rules,
 * slower rate, and verse text split into short chunks with a gentle pause.
 * He can also read his English tips aloud with an en-IN (fallback en-*) voice.
 */

/** Dedicated calm, meditative pacing for sacred ślokas, mantras, and subhāṣitas */
export const SHLOKA_RECITATION_RATE = 0.64;
export const SHLOKA_PADA_PAUSE_MS = 900;
export const SHLOKA_END_PAUSE_MS = 1450;
export const SHLOKA_COMMA_PAUSE_MS = 480;

/**
 * Detects whether text is a Sanskrit śloka, mantra, subhāṣita, or sacred verse.
 * Identified by double daṇḍa (॥), metered couplets, multi-pāda daṇḍa patterns,
 * or sacred invocations (ओं / ॐ, नमोऽस्तु, शान्तिः, etc.).
 */
export const isShlokaOrVerseText = (text: string): boolean => {
  if (!text) return false;
  // Double danda ॥, ||, or ।। indicates end of verse
  if (/[॥]|\|\||।।/.test(text)) return true;
  // Multiple dandas with multi-clause verse length
  if ((text.match(/[।|]/g) || []).length >= 2 && text.length > 20) return true;
  // Single danda with sacred chant / invocation / meter keywords
  if (
    /[।|]/.test(text) &&
    /[ॐओं]|नमोऽस्तु|नमस्तुभ्य|शान्तिः|कल्याण|धीमहि|प्रचोदयात्|सिध्यन्ति|सुभाषित|मङ्गला|ज्योतिर्गमय/u.test(text)
  ) {
    return true;
  }
  // Sacred invocations, stotras, or traditional shloka openings with reasonable length
  if (
    text.length >= 15 &&
    (/[ॐओं]/.test(text) ||
      /नमोऽस्तु|नमस्तुभ्य|वक्रतुण्ड|गुरुर्ब्रह्मा|सरस्वत्यै\s+नमः|शान्तिः\s+शान्तिः|धीमहि|प्रचोदयात्|ज्योतिर्गमय|सुभाषित|विद्या\s+ददाति|वसुधैव\s+कुटुम्बकम्|सन्तु\s+निरामयाः/u.test(
        text,
      ))
  ) {
    return true;
  }
  return false;
};

/** Mac / other: calm teacher pace (kept ≥ 0.72 — lower rates sound gravelly). */
export const BODHI_RATE = 0.75;
/** Windows: the clampRate floor — SAPI voices garble below this. */
export const BODHI_WINDOWS_RATE = 0.75;
/** Natural pitch, same as Varṇamālā playback (safePitch keeps Windows at 1). */
const BODHI_PITCH = 1;
/** Pause between recited chunks (half-verses, phrases). */
export const BODHI_CHUNK_PAUSE_MS = 420;

/** Bodhi voice speed preference (🐢 Slow is the default). */
export type BodhiVoiceSpeed = 'slow' | 'normal';
export type BodhiSpeechLang = 'sa' | 'en';
export const BODHI_SPEED_STORAGE_KEY = 'bodhiVoiceSpeed';

/**
 * Rate + pause per speed and platform.
 * - Slow (default): Mac/other 0.75 + 420ms. Windows can't go below rate 0.75
 *   (clampRate floor), so Slow there uses a longer 600ms pause to feel slower.
 * - Normal: Mac/other 0.85, Windows 0.9, both with a 250ms pause.
 */
const BODHI_SPEED_SETTINGS: Record<BodhiVoiceSpeed, { mac: number; win: number; pauseMac: number; pauseWin: number }> = {
  slow: { mac: BODHI_RATE, win: BODHI_WINDOWS_RATE, pauseMac: BODHI_CHUNK_PAUSE_MS, pauseWin: 600 },
  normal: { mac: 0.85, win: 0.9, pauseMac: 250, pauseWin: 250 },
};

export const getBodhiVoiceSpeed = (): BodhiVoiceSpeed => {
  try {
    if (typeof window !== 'undefined' && window.localStorage?.getItem(BODHI_SPEED_STORAGE_KEY) === 'normal') {
      return 'normal';
    }
  } catch {
    /* storage unavailable (private mode) */
  }
  return 'slow';
};

export const setBodhiVoiceSpeed = (speed: BodhiVoiceSpeed): void => {
  try {
    window.localStorage?.setItem(BODHI_SPEED_STORAGE_KEY, speed);
  } catch {
    /* storage unavailable */
  }
};

export const bodhiVoiceSettings = (
  speed: BodhiVoiceSpeed = getBodhiVoiceSpeed(),
): { rate: number; pauseMs: number } => {
  const s = BODHI_SPEED_SETTINGS[speed] || BODHI_SPEED_SETTINGS.slow;
  return isWindowsPlatform()
    ? { rate: clampRate(s.win), pauseMs: s.pauseWin }
    : { rate: s.mac, pauseMs: s.pauseMac };
};

/** A piece of the original text, tagged with its spoken chunk index (or null). */
export interface BodhiSegment {
  text: string;
  chunkIndex: number | null;
}

const SANSKRIT_BREAK = /([।॥\n\r,;!?]+)/;

const cleanSanskritChunk = (chunk: string): string =>
  chunk
    // Drop Devanagari verse numbers, dashes, quotes, exclamation marks and brackets.
    .replace(/[।॥०-९0-9()[\]{}<>'"“”‘’\-–—|!]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const cleanEnglishChunk = (chunk: string): string =>
  chunk
    // English voice should not attempt Devanagari: drop "(शून्य)" glosses
    // and any stray Devanagari words, then tidy leftover quotes/brackets.
    .replace(/\([^)]*[\u0900-\u097F][^)]*\)/g, ' ')
    .replace(/[\u0900-\u097F]+/g, ' ')
    .replace(/["“”]\s*["“”]/g, ' ')
    .replace(/[“”"()[\]{}<>]+/g, ' ')
    // Convert exclamation marks to periods so TTS engines never speak "exclamation mark"
    .replace(/!+/g, '.')
    .replace(/\s+([,.;:?])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Split text into display segments, each tagged with the chunk index that
 * speakAsBodhi() will report via onChunk. Joining every segment's text gives
 * back the original string, so the UI can render it with a highlight.
 * Sanskrit: chunks on । ॥ newline , ; ! ?   English: chunks on . ! ? / newline.
 */
export const splitBodhiSegments = (text: string, lang: BodhiSpeechLang = 'sa'): BodhiSegment[] => {
  const segments: BodhiSegment[] = [];
  let next = 0;
  if (lang === 'en') {
    const parts = (text || '').match(/[^.!?\n]+(?:[.!?]+["”’')\]]*)?\s*|[.!?\n]+\s*/g) || [];
    for (const part of parts) {
      const cleaned = cleanEnglishChunk(part);
      segments.push({ text: part, chunkIndex: /[a-z]/i.test(cleaned) ? next++ : null });
    }
    return segments;
  }
  for (const part of (text || '').split(SANSKRIT_BREAK)) {
    if (!part) continue;
    if (SANSKRIT_BREAK.test(part) && /^[।॥\n\r,;!?]+$/.test(part)) {
      segments.push({ text: part, chunkIndex: null });
      continue;
    }
    const cleaned = cleanSanskritChunk(part);
    segments.push({ text: part, chunkIndex: cleaned && isSanskritText(cleaned) ? next++ : null });
  }
  return segments;
};

const chunksFromSegments = (text: string, lang: BodhiSpeechLang): string[] =>
  splitBodhiSegments(text, lang)
    .filter((seg) => seg.chunkIndex !== null)
    .map((seg) => (lang === 'en' ? cleanEnglishChunk(seg.text) : cleanSanskritChunk(seg.text)));

/**
 * Split text into recitation chunks on danda (। ॥), newlines, commas and
 * sentence marks (! ?). Keeps spaces inside a chunk so words stay separate.
 */
export const splitBodhiChunks = (text: string): string[] => chunksFromSegments(text, 'sa');

/** English tips: chunk on sentence boundaries (. ! ?). */
export const splitBodhiEnglishChunks = (text: string): string[] => chunksFromSegments(text, 'en');

/** Max characters per utterance; keeps each well under Chrome's ~15 s cutoff. */
const BODHI_MAX_UTTERANCE_CHARS = 160;

/** Split an over-long chunk at commas/semicolons/dashes, then at word boundaries. */
export const splitLongSpeechChunk = (chunk: string, max = BODHI_MAX_UTTERANCE_CHARS): string[] => {
  const text = (chunk || '').trim();
  if (text.length <= max) return text ? [text] : [];
  const out: string[] = [];
  let current = '';
  const push = () => {
    if (current.trim()) out.push(current.trim());
    current = '';
  };
  // No regex lookbehind: older Safari cannot parse it.
  const clauses = text.replace(/([,;:—–])\s+/g, '$1\u0000').split('\u0000');
  for (const clause of clauses) {
    if ((current + ' ' + clause).trim().length <= max) {
      current = (current + ' ' + clause).trim();
      continue;
    }
    push();
    if (clause.length <= max) {
      current = clause;
      continue;
    }
    for (const word of clause.split(/\s+/)) {
      if ((current + ' ' + word).trim().length > max) push();
      current = (current + ' ' + word).trim();
    }
  }
  push();
  return out;
};

/** Per-word visarga echo (same rule as the letter/word path) + vocalic vowel Mac/Win cues. */
const toBodhiSpeech = (chunk: string): string => {
  const spaced = isWindowsPlatform();
  return chunk
    .split(' ')
    .map((word) => {
      const bare = word.replace(/[।॥,;:!?()[\]{}<>'"“”‘’\-–—|!]+/g, '').trim();

      // Māheśvara Sūtra sound clusters: expand to spaced sounds so they never blend into words
      if (bare === 'अइउण्') return 'अ इ उ ण्';
      if (bare === 'ऋऌक्') return isMacDesktopPlatform() ? 'रि ल्रि क्' : 'ऋ लृ क्';
      if (bare === 'एओङ्') return 'ए ओ ङ्';
      if (bare === 'ऐऔच्') return 'ऐ औ च्';
      if (bare === 'हयवरट्') return 'ह य व र ट्';
      if (bare === 'लण्') return 'ल ण्';
      if (bare === 'ञमङणनम्') return 'ञ म ङ ण न म्';
      if (bare === 'झभञ्') return 'झ भ ञ्';
      if (bare === 'घढधष्') return 'घ ढ ध ष्';
      if (bare === 'जबगडदश्') return 'ज ब ग ड द श्';
      if (bare === 'खफछठथचटतव्') return 'ख फ छ ठ थ च ट त व्';
      if (bare === 'कपय्') return 'क प य्';
      if (bare === 'शषसर्') return 'श ष स र्';
      if (bare === 'हल्') return 'ह ल्';

      if (isMacDesktopPlatform() && MAC_VOCALIC_CUES[bare]) {
        return word.replace(bare, MAC_VOCALIC_CUES[bare].cue);
      }
      if (bare === 'ऌ') {
        return word.replace('ऌ', isMacDesktopPlatform() ? 'ल्रि' : 'लृ');
      }
      return applyVisargaEcho(word, spaced);
    })
    .join(' ');
};

const BODHI_ENGLISH_RATE = 0.92;
/** True when a chunk is Latin-script English with no Devanagari. */
const isEnglishProse = (chunk: string): boolean =>
  !/[\u0900-\u097F]/.test(chunk) && (chunk.match(/[A-Za-z]/g) || []).length >= 4;

/** English voice only (en-IN preferred, then any en-*). Never a Hindi voice. */
const pickBodhiEnglishVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | undefined =>
  pickEnglishCueVoice(voices.filter((v) => (v.lang || '').toLowerCase().replace('_', '-').startsWith('en')));

/**
 * Sanskrit: reuse the exact Varṇamālā letter/word configuration
 * (configureUtterance → pickHindiVoice, same hi-IN preference order on every
 * platform, Devanagari text), then apply only Bodhi's slow rate and natural
 * pitch 1 via safePitch. English tips: separate gentle en-IN voice, pitch 1.
 */
const configureBodhiUtterance = (
  utterance: SpeechSynthesisUtterance,
  lang: BodhiSpeechLang,
  rate: number,
  chunk: string,
): void => {
  const allVoices = window.speechSynthesis.getVoices();
  // Script decides the voice: Latin-only prose is English even when the caller
  // passed 'sa' — a Hindi voice reading English sounds badly mangled.
  if (lang === 'sa' && isEnglishProse(chunk)) lang = 'en';
  if (lang === 'en' && /[\u0900-\u097F]/.test(chunk) && !/[A-Za-z]{3,}/.test(chunk)) lang = 'sa';
  // The Bodhi role voice can be English (Rishi, Ravi) or Hindi (Swara, Lekha).
  // Only use it when its language matches the text: an English voice given
  // Devanagari reads nothing or a single word, and a Hindi voice garbles English.
  const roleVoice = resolveVoiceForRole('bodhi', allVoices);
  const roleLang = (roleVoice?.lang || '').toLowerCase().replace('_', '-');
  const roleIsEnglish = roleLang.startsWith('en');
  const roleIsIndic = /^(hi|sa|mr|ne)/.test(roleLang);
  const configuredBodhiVoice =
    roleVoice && (lang === 'en' ? roleIsEnglish : roleIsIndic) ? roleVoice : undefined;
  if (lang === 'en') {
    const voice = configuredBodhiVoice || pickBodhiEnglishVoice(allVoices);
    utterance.voice = voice || null;
    utterance.lang = voice?.lang || 'en-IN';
  } else {
    if (configuredBodhiVoice) {
      utterance.voice = configuredBodhiVoice;
      utterance.lang = configuredBodhiVoice.lang || 'hi-IN';
    } else {
      configureUtterance(utterance, chunk, utterance.text);
    }
  }
  // English prose sounds chopped at the slow Sanskrit recitation pace.
  utterance.rate = clampRate(lang === 'en' ? Math.max(rate, BODHI_ENGLISH_RATE) : rate);
  utterance.pitch = safePitch(BODHI_PITCH);
  utterance.volume = 1;
};

export interface SpeakAsBodhiOptions {
  onEnd?: () => void;
  /** Fired just before each chunk is spoken (follow-along highlight). */
  onChunk?: (index: number, chunkText: string) => void;
  /** Override the pause between chunks (defaults from speed). */
  pauseMs?: number;
  /** Force a speed; otherwise the stored preference is read per chunk. */
  speed?: BodhiVoiceSpeed;
  /** Force a specific speech rate (e.g. 0.70 for deliberate letter articulation). */
  rate?: number;
  /** 'sa' (default, hi-IN voice) or 'en' (en-IN / en-* voice). */
  lang?: BodhiSpeechLang;
}

/**
 * Speak as Bodhi: cancels any in-progress speech, then recites chunk by chunk
 * at a slow pace. onEnd fires exactly once (finished, stopped, or aborted by
 * another playPronunciation/stopPronunciation). Returns stop().
 */
export const speakAsBodhi = (text: string, options?: SpeakAsBodhiOptions): (() => void) => {
  const lang: BodhiSpeechLang = options?.lang ?? 'sa';
  const isShloka = lang === 'sa' && isShlokaOrVerseText(text || '');
  const segments = splitBodhiSegments(text || '', lang);
  const chunks = chunksFromSegments(text || '', lang);
  let ended = false;
  const finish = () => {
    if (ended) return;
    ended = true;
    options?.onEnd?.();
  };

  if (!chunks.length || typeof window === 'undefined' || !window.speechSynthesis) {
    finish();
    return () => undefined;
  }

  // Speed is resolved per chunk so toggling 🐢 Slow / Normal mid-verse
  // applies from the next chunk.
  const currentSettings = () => bodhiVoiceSettings(options?.speed ?? getBodhiVoiceSpeed());
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let index = 0;
  let settledForIndex = -1;

  const clearTimers = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    clearTimers();
    stopPronunciation();
    finish();
  };

  stopPronunciation();
  const gen = speakGeneration;

  const speakNext = () => {
    if (cancelled) return;
    if (gen !== speakGeneration) {
      cancelled = true;
      clearTimers();
      finish();
      return;
    }
    if (index >= chunks.length) {
      clearTimers();
      finish();
      return;
    }
    const chunk = chunks[index];
    const chunkIndex = index;
    index += 1;
    const settings = currentSettings();
    const effectiveRate = options?.rate ?? (isShloka ? SHLOKA_RECITATION_RATE : settings.rate);

    // Find the trailing punctuation immediately following this chunk in segments
    let trailingPunct = '';
    const segIdx = segments.findIndex((s) => s.chunkIndex === chunkIndex);
    if (segIdx >= 0) {
      for (let j = segIdx + 1; j < segments.length; j++) {
        if (segments[j].chunkIndex === null) {
          trailingPunct += segments[j].text;
        } else {
          break;
        }
      }
    }

    const after = () => {
      if (cancelled || settledForIndex === chunkIndex) return;
      settledForIndex = chunkIndex;
      clearTimers();
      if (gen !== speakGeneration) {
        cancelled = true;
        finish();
        return;
      }
      let pauseMs: number;
      if (options?.pauseMs != null) {
        pauseMs = options.pauseMs;
      } else if (isShloka) {
        if (/[॥]|\|\||।।/.test(trailingPunct)) {
          pauseMs = SHLOKA_END_PAUSE_MS;
        } else if (/[।|]/.test(trailingPunct) || /\n/.test(trailingPunct)) {
          pauseMs = SHLOKA_PADA_PAUSE_MS;
        } else if (/[,;]/.test(trailingPunct)) {
          pauseMs = SHLOKA_COMMA_PAUSE_MS;
        } else {
          pauseMs = SHLOKA_PADA_PAUSE_MS;
        }
      } else {
        pauseMs = currentSettings().pauseMs;
      }
      timer = setTimeout(speakNext, chunkIndex + 1 < chunks.length ? pauseMs : 0);
    };

    // Chrome/Edge cut an utterance off after ~15 s, so long sentences are
    // spoken as a few shorter pieces, queued once, in order.
    const pieces = splitLongSpeechChunk(chunk);
    try {
      options?.onChunk?.(chunkIndex, chunk);
    } catch {
      /* UI callback errors must not break recitation */
    }
    const perChar = lang === 'en' ? 120 : 220;
    let fallbackMs = 0;
    pieces.forEach((piece, pieceIndex) => {
      const utterance = new SpeechSynthesisUtterance(lang === 'en' ? piece : toBodhiSpeech(piece));
      configureBodhiUtterance(utterance, lang, effectiveRate, piece);
      if (pieceIndex === pieces.length - 1) {
        utterance.onend = after;
      }
      // An error on any piece ends this chunk (cancel() during stop also lands here).
      utterance.onerror = after;
      fallbackMs += Math.max(2500, Math.round((piece.length * perChar) / utterance.rate));
      window.speechSynthesis.speak(utterance);
    });
    // Some Chrome/Edge builds skip onend; advance after a generous upper
    // bound proportional to the chunk length at Bodhi's slow rate.
    fallbackTimer = setTimeout(after, fallbackMs);
    // Chrome sometimes leaves the queue paused after cancel(); nudge it.
    try {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    } catch {
      /* ignore */
    }
  };

  void whenVoicesReady().then(() => {
    if (cancelled) return;
    speakNext();
  });
  return stop;
};

/** Stop Bodhi (or any) speech. */
export const stopBodhiSpeech = (): void => stopPronunciation();
